import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Stack,
  Typography,
  Button,
  Grow,
  Fade,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  InputAdornment,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  Snackbar,
  Alert,
} from '@mui/material';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { Link } from 'react-router-dom';
import { functions } from '../../firebase/firebase';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import LetterCard from '../../components/LetterCard/LetterCard';
import LetterReaderModal from '../../components/LetterReaderModal/LetterReaderModal';
import AuthenticationModal from '../../components/AuthenticationModal/AuthenticationModal';

const linkStyle = {
  color: 'inherit',
  textDecoration: 'none',
};

const ctaHover = {
  fontWeight: 'bold',
  '&:hover': {
    backgroundColor: 'blue',
    transform: 'scale(1.05)',
  },
};

export default function MyLetters() {
  const [authResolved, setAuthResolved] = useState(false);
  const [signedIn, setSignedIn] = useState(false);

  const [letters, setLetters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selected, setSelected] = useState(null);
  const [readerOpen, setReaderOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [actionError, setActionError] = useState(null);

  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('newest');
  const q = query.trim().toLowerCase();
  const visibleLetters = letters
    .filter((l) => {
      if (!q) return true;
      return [l.name, l.generatedLetter, l.jobInfo, l.companyInfo]
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(q));
    })
    .sort((a, b) => {
      if (sort === 'name') return (a.name || '').localeCompare(b.name || '');
      const cmp = (a.createdAt || '').localeCompare(b.createdAt || '');
      return sort === 'oldest' ? cmp : -cmp;
    });

  const fetchLetters = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const getUserLetters = httpsCallable(functions, 'getUserLetters');
      const res = await getUserLetters();
      if (res.data.error) {
        setError(res.data.error);
      } else {
        setLetters(res.data.letters || []);
      }
    } catch (err) {
      console.log(err);
      setError('Something went wrong loading your letters. Please try again.');
    }
    setLoading(false);
  }, []);
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setAuthResolved(true);
      setSignedIn(!!user);
      if (user) {
        fetchLetters();
      } else {
        setLetters([]);
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, [fetchLetters]);

  const handleOpen = (letter) => {
    setSelected(letter);
    setReaderOpen(true);
  };

  const handleRename = async (id, name) => {
    setRenaming(true);
    try {
      const renameUserLetter = httpsCallable(functions, 'renameUserLetter');
      const res = await renameUserLetter({ letterId: id, name });
      if (res.data.error || typeof res.data.name !== 'string') {
        setActionError(res.data.error || 'Could not rename. Please try again.');
      } else {
        setLetters((prev) =>
          prev.map((l) => (l.id === id ? { ...l, name: res.data.name } : l)),
        );
        setSelected((prev) =>
          prev && prev.id === id ? { ...prev, name: res.data.name } : prev,
        );
      }
    } catch (err) {
      console.log(err);
      setActionError('Could not rename. Please try again.');
    }
    setRenaming(false);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const deleteUserLetter = httpsCallable(functions, 'deleteUserLetter');
      const res = await deleteUserLetter({ letterId: deleteTarget.id });
      if (res.data.error) {
        setActionError(res.data.error);
      } else {
        setLetters((prev) => prev.filter((l) => l.id !== deleteTarget.id));
        if (selected && selected.id === deleteTarget.id) {
          setReaderOpen(false);
        }
      }
    } catch (err) {
      console.log(err);
      setActionError('Could not delete. Please try again.');
    }
    setDeleting(false);
    setDeleteTarget(null);
  };

  const renderBody = () => {
    // signed out
    if (authResolved && !signedIn) {
      return (
        <EmptyState
          title="Sign in to see your cover letters"
          subtitle="Your generated cover letters are saved to your account so you can revisit, rename, and reuse them anytime."
          action={
            <Button
              variant="contained"
              color="primary"
              size="large"
              sx={ctaHover}
              onClick={() => setAuthModalOpen(true)}
            >
              Register / Sign in
            </Button>
          }
        />
      );
    }

    // loading
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress />
        </Box>
      );
    }

    // error
    if (error) {
      return (
        <EmptyState
          title="We couldn't load your letters"
          subtitle={error}
          action={
            <Button
              variant="contained"
              color="primary"
              size="large"
              sx={ctaHover}
              onClick={fetchLetters}
            >
              Try again
            </Button>
          }
        />
      );
    }

    // empty library (nothing saved yet)
    if (letters.length === 0) {
      return (
        <EmptyState
          title="No saved letters yet"
          subtitle="When you generate a cover letter, tap the bookmark icon to save it here. Nothing is saved until you choose to."
          action={
            <Link to="/create" style={linkStyle}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                sx={ctaHover}
              >
                Generate a Cover Letter
              </Button>
            </Link>
          }
        />
      );
    }

    // gallery with search + sort toolbar
    return (
      <>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 3 }}
        >
          <TextField
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, company, role, or text…"
            size="small"
            sx={{ flexGrow: 1, maxWidth: { sm: 460 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: query ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setQuery('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <ToggleButtonGroup
            value={sort}
            exclusive
            size="small"
            onChange={(e, val) => val && setSort(val)}
            aria-label="sort letters"
          >
            <ToggleButton value="newest">Newest</ToggleButton>
            <ToggleButton value="oldest">Oldest</ToggleButton>
            <ToggleButton value="name">A–Z</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {visibleLetters.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
            <Typography variant="h6" component="p" gutterBottom>
              No letters match "{query}"
            </Typography>
            <Button onClick={() => setQuery('')}>Clear search</Button>
          </Box>
        ) : (
          <Stack
            direction="row"
            flexWrap="wrap"
            gap={3}
            justifyContent={{ xs: 'center', md: 'flex-start' }}
          >
            {visibleLetters.map((letter, index) => (
              <Grow
                key={letter.id}
                in={true}
                style={{ transformOrigin: 'center top' }}
                timeout={Math.min(400 + index * 150, 1200)}
              >
                <Box sx={{ display: 'flex' }}>
                  <LetterCard
                    letter={letter}
                    onOpen={handleOpen}
                    onRename={handleRename}
                    onDelete={setDeleteTarget}
                    renaming={renaming}
                  />
                </Box>
              </Grow>
            ))}
          </Stack>
        )}
      </>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Header />
      <Fade in={true} timeout={1000}>
        <Container maxWidth="lg" sx={{ py: 4, flexGrow: 1 }}>
          <Box sx={{ mb: 4, textAlign: { xs: 'center', md: 'left' } }}>
            <Typography variant="h3" component="h1" gutterBottom>
              My Cover Letters
            </Typography>
            <Typography variant="h6" component="p" color="text.secondary">
              {signedIn && letters.length > 0
                ? `${letters.length} saved ${
                    letters.length === 1 ? 'letter' : 'letters'
                  } — click any card to read, rename, or copy it.`
                : 'Your personal library of AI-generated cover letters.'}
            </Typography>
          </Box>
          {renderBody()}
        </Container>
      </Fade>
      <Footer />

      <LetterReaderModal
        open={readerOpen}
        handleClose={() => setReaderOpen(false)}
        letter={selected}
        onRename={handleRename}
        renaming={renaming}
      />

      <AuthenticationModal
        open={authModalOpen}
        handleClose={() => setAuthModalOpen(false)}
      />

      <Dialog
        open={!!deleteTarget}
        onClose={() => !deleting && setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', padding: '.5rem' } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          Delete this cover letter?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            "{deleteTarget?.name}" will be permanently removed. This can't be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteTarget(null)}
            color="inherit"
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Delete'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!actionError}
        autoHideDuration={5000}
        onClose={() => setActionError(null)}
      >
        <Alert
          onClose={() => setActionError(null)}
          severity="error"
          sx={{ width: '100%', maxWidth: 420 }}
        >
          {actionError}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function EmptyState({ title, subtitle, action }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        py: { xs: 6, md: 10 },
        px: 2,
      }}
    >
      <DescriptionOutlined
        sx={{ fontSize: 72, color: 'primary.main', opacity: 0.5, mb: 2 }}
      />
      <Typography variant="h5" component="p" gutterBottom sx={{ fontWeight: 'bold' }}>
        {title}
      </Typography>
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ maxWidth: 460, mb: 3 }}
      >
        {subtitle}
      </Typography>
      {action}
    </Box>
  );
}
